import { Checkbox, Label, MultiSelect, PageTabsContent } from "@tellescope/ui";
import { useTranslation } from "react-i18next";
import { useOrgFeatures } from "../../../../permissions";
import { FieldRestrictionList } from "./FieldRestrictionList";
import { UI_RESTRICTION_TOGGLES, VISIBLE_INTEGRATION_VALUES } from "./roleEditorConfig";
import { RoleEditor } from "./useEditRoleDialog";
import { useRoleEditorLabels } from "./useRoleEditorLabels";

export interface UIRestrictionsTabContentProps {
  editor: RoleEditor;
}

export const UIRestrictionsTabContent = ({ editor }: UIRestrictionsTabContentProps) => {
  const { t } = useTranslation("settings");
  const labels = useRoleEditorLabels();
  const { isFeatureEnabled } = useOrgFeatures();
  const { draft, toggleUIRestriction, setVisibleIntegrations } = editor;
  const visibleToggles = UI_RESTRICTION_TOGGLES.filter((toggle) => !toggle.feature || isFeatureEnabled(toggle.feature));

  return (
    <PageTabsContent
      value="ui-restrictions"
      className="px-6 py-4 space-y-6 overflow-y-auto"
      style={{ maxHeight: "min(80vh, 900px)" }}
    >
      <p className="text-xs text-muted-foreground">
        {t("These hide pages, buttons, and UI elements. They do not grant or remove data access.")}
      </p>

      <div className="grid grid-cols-1 gap-x-6 gap-y-3 sm:grid-cols-2">
        {visibleToggles.map(({ key }) => (
          <label key={key} className="flex items-start gap-2 text-sm">
            <Checkbox
              checked={!!draft.uiRestrictions[key]}
              onCheckedChange={(checked) => toggleUIRestriction(key, checked === true)}
              className="mt-0.5"
            />
            <span>{labels.toggles[key]}</span>
          </label>
        ))}
      </div>

      <div className="space-y-1.5 max-w-md">
        <Label htmlFor="visible-integrations">{t("Visible Integrations (leave blank for all)")}</Label>
        <MultiSelect
          id="visible-integrations"
          options={VISIBLE_INTEGRATION_VALUES.map((value) => ({ value, label: labels.integrations[value] ?? value }))}
          value={draft.uiRestrictions.visibleIntegrations ?? []}
          onValueChange={setVisibleIntegrations}
          placeholder={t("All integrations")}
        />
      </div>
      <div className="space-y-4 border-t pt-4">
        <h3 className="text-lg font-medium">{t("Field Restrictions")}</h3>
        <FieldRestrictionList
          list="hiddenFields"
          label={t("Hidden")}
          description={t("Fields hidden from the UI for this role. First Name and Last Name cannot be hidden.")}
          editor={editor}
        />
        <FieldRestrictionList
          list="disabledFields"
          label={t("Updates Disabled")}
          description={t("Fields visible but not editable by this role.")}
          editor={editor}
        />
      </div>
    </PageTabsContent>
  );
};
